import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Form, Icon, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import Button from '../../../../../components/Button';
import { iRootState } from '../../../../../store';
import { AvailableBrokersAndEstatesState } from '../../../../../store/types/temp-types';
import { uploadYelumBatchImport } from '../../../../../services/yelum-batch-imports';

const FileInput = styled.input`
  display: none;
`;

const SelectedFile = styled.div`
  margin-top: 8px;
  color: #555;
  word-break: break-word;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
};

const NewYelumImportModal: React.FC<Props> = ({
  open,
  onClose,
  availableBrokersAndEstates
}) => {
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const [estateId, setEstateId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const estateOptions = useMemo(
    () =>
      (availableBrokersAndEstates.estates || []).map(estate => ({
        key: estate.key,
        text: estate.text,
        value: String(estate.value?.id || estate.value)
      })),
    [availableBrokersAndEstates.estates]
  );

  useEffect(() => {
    if (open) {
      setError('');
      setEstateId('');
      setFile(null);
    }
  }, [open]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setError('');

    if (selectedFile && !/\.xlsx$/i.test(selectedFile.name)) {
      setFile(null);
      setError('Selecione um arquivo no formato .xlsx.');
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async (event?: { preventDefault: () => void }) => {
    event?.preventDefault();
    if (!estateId) {
      setError('Selecione a imobiliária.');
      return;
    }
    if (!file) {
      setError('Selecione a planilha XLSX.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await uploadYelumBatchImport(file, estateId);
      const importId = response?.id || response?.data?.id;
      if (!importId) {
        throw new Error('A importação foi recebida, mas o identificador não foi retornado.');
      }

      onClose();
      history.push(`/admin/fire-insurance-batches/yelum-imports/${importId}`);
    } catch (uploadError) {
      const body = (uploadError as any)?.response?.data || uploadError;
      setError(
        body?.message ||
          (uploadError as Error)?.message ||
          'Não foi possível enviar a planilha. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose} size="small">
      <Modal.Header>
        <Icon name="cloud upload" color="purple" /> Nova importação de lote Yelum
      </Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          {error && (
            <Message negative>
              <Message.Header>Não foi possível iniciar a importação</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <Form.Select
            fluid
            search
            required
            label="Imobiliária"
            placeholder="Selecione a imobiliária"
            options={estateOptions}
            value={estateId}
            disabled={loading}
            onChange={(_, data) => setEstateId(String(data.value || ''))}
          />

          <Form.Field required>
            <label htmlFor="yelum-import-file">Planilha XLSX</label>
            <FileInput
              ref={inputRef}
              id="yelum-import-file"
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
              disabled={loading}
            />
            <Button
              type="button"
              basic
              color="purple"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
            >
              <Icon name="file excel outline" />
              Selecionar arquivo
            </Button>
            {file && <SelectedFile>{file.name}</SelectedFile>}
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" basic onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          type="button"
          color="purple"
          loading={loading}
          disabled={loading || !estateId || !file}
          onClick={handleSubmit}
        >
          <Icon name="upload" />
          Enviar e revisar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const mapStateToProps = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

export default connect(mapStateToProps)(NewYelumImportModal);
